import {recordRumAction} from '../../util/datadog/datadogRum';
import {makeTrackEvent} from './makeTrackEvent';

export type RegistryImportEntryPoint =
  | 'getting_started'
  | 'landing_header'
  | 'landing_overflow_menu'
  | 'success_toast';

export type RegistryImportStep = 'source' | 'destination' | 'review';

export type TrackRegistryImportEvent = {
  action:
    | 'import_cta_clicked'
    | 'import_step_viewed'
    | 'import_submitted'
    | 'import_started'
    | 'import_start_failed'
    | 'import_activity_opened';
  source: 'huggingface';
  location?: RegistryImportEntryPoint;
  step?: RegistryImportStep;
  entityName?: string;
  projectName?: string;
  registryName?: string;
  collectionName?: string;
  hfRepoId?: string;
  hfRevision?: string;
  importId?: string;
  errorMessage?: string;
  /** Milliseconds from opening the drawer to the tracked action. */
  durationMs?: number;
};

const trackRegistryImportInteraction = makeTrackEvent<
  TrackRegistryImportEvent,
  {
    action: {
      description: 'The import workflow action.';
      exampleValues: [
        'import_cta_clicked',
        'import_step_viewed',
        'import_submitted',
        'import_started',
        'import_start_failed',
        'import_activity_opened',
      ];
    };
    source: {
      description: 'Where the model is imported from.';
      exampleValues: ['huggingface'];
    };
    location: {
      description: 'UI surface that triggered the action.';
      exampleValues: ['getting_started', 'landing_header'];
    };
    step: {
      description: 'Drawer step, for step-level actions.';
      exampleValues: ['source', 'destination', 'review'];
    };
    entityName: {
      description: 'Team that owns the imported artifact.';
      exampleValues: ['<name>'];
    };
    projectName: {
      description: 'Team project that owns the imported artifact.';
      exampleValues: ['huggingface-imports'];
    };
    registryName: {
      description: 'Registry the import links into.';
      exampleValues: ['huggingface-models'];
    };
    collectionName: {
      description: 'Registry collection the import links into.';
      exampleValues: ['Qwen3.5-0.8B-GGUF'];
    };
    hfRepoId: {
      description: 'Hugging Face repo being imported.';
      exampleValues: ['ggml-org/Qwen3.5-0.8B-GGUF'];
    };
    hfRevision: {
      description: 'Requested Hugging Face revision.';
      exampleValues: ['main'];
    };
    importId: {
      description: 'GraphQL id of the created import, when known.';
      exampleValues: ['UmVnaXN0cnlIdWdnaW5nRmFjZUltcG9ydDo0'];
    };
    errorMessage: {
      description: 'Server error message when the import failed to start.';
      exampleValues: ['permission denied'];
    };
    durationMs: {
      description: 'Milliseconds from opening the drawer to the action.';
      exampleValues: [12345];
    };
    _description: 'Tracks the Registry import workflow from CTA to submission.';
    _location: 'Registry landing page and import drawers';
    _motivation: 'Measure funnel drop-off and failure rate of Registry imports.';
  }
>('Registry Import Interaction');

export function trackRegistryImportEvent(
  eventData: TrackRegistryImportEvent
): void {
  trackRegistryImportInteraction(eventData);
  recordRumAction(`RegistryImport.${eventData.action}`, eventData);
}
